import React, { useEffect, useState, useCallback } from 'react'

/**
 * TemplateViewer - Renders published templates from DigitalOcean Spaces
 * This component is used when accessing the app via *.users.godreamx.com
 */
function TemplateViewer({ orgName }) {
  const [template, setTemplate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTemplateFromSpaces = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Construct the URL to fetch template from DO Spaces
      // For development/preview mode (production uses users-app)
      const spacesBaseUrl = import.meta.env.VITE_DO_SPACES_CDN_URL || 'https://dreamxwebbuilder.nyc3.cdn.digitaloceanspaces.com'
      const folderPrefix = import.meta.env.VITE_DO_SPACES_FOLDER_PREFIX || 'development'
      const templateUrl = `${spacesBaseUrl}/${folderPrefix}/templates/${orgName}/index.html`

      console.log(`🌐 Fetching template from: ${templateUrl}`)

      const response = await fetch(templateUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml'
        }
      })

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Template not found for organization "${orgName}". Please ensure the template has been published.`)
        }
        throw new Error(`Failed to load template: ${response.status} ${response.statusText}`)
      }

      const htmlContent = await response.text()
      setTemplate(htmlContent)
      console.log(`✅ Template loaded successfully for ${orgName}`)

    } catch (err) {
      console.error('❌ Failed to fetch template:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [orgName])

  useEffect(() => {
    fetchTemplateFromSpaces()
  }, [fetchTemplateFromSpaces])

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading template for {orgName}...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="max-w-md p-8 bg-gray-800 rounded-lg shadow-xl text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-white mb-4">Template Not Found</h1>
          <p className="text-gray-300 mb-6">{error}</p>
          <div className="text-sm text-gray-400">
            <p>Organization: <span className="font-mono text-blue-400">{orgName}</span></p>
          </div>
        </div>
      </div>
    )
  }

  // Always use iframe mode for development/preview in template editor
  // Direct mode would replace the entire editor page
  const useIframe = true

  // Note: Direct render mode is disabled in template editor to prevent replacing the editor
  // Production serving uses users-app which doesn't need this component

  // Iframe mode: Render in isolated iframe (better for development)
  if (useIframe) {
    return (
      <div className="template-viewer-container" style={{ width: '100%', height: '100vh', margin: 0, padding: 0 }}>
        <iframe
          srcDoc={template}
          title={`Template for ${orgName}`}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            margin: 0,
            padding: 0
          }}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
        />
      </div>
    )
  }

  // Direct mode: Return null since we're replacing the entire document
  return null
}

export default TemplateViewer
